import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheetHeader from './SheetHeader';

export default function BottomSheet({
  open = false,
  busy = false,
  title,
  subtitle = null,
  onClose,
  onBack,
  testId = 'bottom-sheet',
  children,
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setClosing(false);
    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const { body, documentElement } = document;
    const previous = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyTouchAction: body.style.touchAction,
      docOverflow: documentElement.style.overflow,
      docOverscrollBehavior: documentElement.style.overscrollBehavior,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.touchAction = 'none';
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    return () => {
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.width = previous.bodyWidth;
      body.style.touchAction = previous.bodyTouchAction;
      documentElement.style.overflow = previous.docOverflow;
      documentElement.style.overscrollBehavior = previous.docOverscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  const requestClose = () => {
    if (busy) return;
    setClosing(true);
    setVisible(false);
    window.setTimeout(onClose, 180);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end" data-testid={testId}>
      <button
        type="button"
        aria-label={t('common.close')}
        className={[
          'absolute inset-0 bg-black/[0.64] transition-opacity duration-200 ease-out',
          visible && !closing ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        onClick={requestClose}
      />
      <div
        className={[
          'relative z-10 w-full rounded-t-home-24 bg-popup-gradient px-6 pt-6',
          'transform-gpu transition-transform duration-200 ease-out',
          visible && !closing ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
        style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 24px)' }}
      >
        <SheetHeader
          title={title}
          subtitle={subtitle}
          onClose={requestClose}
          onBack={onBack ? () => {
            if (!busy) onBack();
          } : undefined}
        />
        {children}
      </div>
    </div>
  );
}
